import { io, Socket } from "socket.io-client";
import { store } from "../store";
import {
  addNotification,
  incrementReconnectAttempts,
  removeNotification,
  resetReconnectAttempts,
  setConnectionStatus,
} from "../store/slices/notificationsSlice";
import { ConnectionStatus, INotification } from "../types/notifications";
import { getAccessToken } from "../utils/auth";
import { logger, captureException } from "../utils/logger";
import { StreamType } from "../types/websocket";

// Реэкспорт для использования в других файлах
export { ConnectionStatus };

/**
 * WebSocket сервис для real-time уведомлений Event Master
 * Исправленная версия без ошибок TypeScript
 */

// Типы событий для уведомлений
export interface NotificationEvent {
  id: string;
  type: string;
  title: string;
  message: string;
  userId: string;
  createdAt: string;
  read: boolean;
}

// Типы событий для аналитики
export interface AnalyticsEvent {
  metric: string;
  value: number;
  timestamp: string;
  metadata?: Record<string, any>;
}

// Типы для стриминга метрик
export interface AnalyticsMetricUpdate {
  metric: string;
  value: number;
  previousValue?: number;
  change?: number;
  changePercent?: number;
  timestamp: string;
  eventId?: string;
  userId?: string;
  metadata?: Record<string, any>;
}

export interface AnalyticsBatchUpdate {
  metrics: AnalyticsMetricUpdate[];
  batchId: string;
  timestamp: string;
  source: "realtime" | "scheduled" | "manual";
}

// ===== Совместимость с удалённым нативным websocket.ts (ADR-004 / XS-011) =====
// Типы и константы перенесены сюда, чтобы единый socket.io-клиент оставался
// единственной точкой входа для потребителей real-time.

/** Generic-сообщение (использует LiveEventPage chat/poll actions и notification mark-read). */
export interface WebSocketMessage {
  type: string;
  data: any;
  timestamp?: Date;
  id?: string;
  priority?: number; // 0-100, выше = важнее
}

/**
 * AD-518. Ответ сервера отправителю реплики в чат события — зеркало BE
 * `ChatMessageAck` (`live-event-websocket.interfaces.ts`). `id` — id записи в
 * `chat_messages`, тот же, что у эха `chat:message`. `code` — машинная причина
 * отказа (`CHAT_RATE_LIMITED`, `CHAT_ACCESS_DENIED`); текст `error` — для людей
 * и на языке сервера, экран подпись выбирает по коду.
 */
export type ChatMessageAck =
  | { status: "ok"; id: string; timestamp: string }
  | { status: "error"; error: string; code?: string; retryAfterMs?: number };

/**
 * Сколько ждать ack. Сервер отвечает за миллисекунды (живая проба 25.09);
 * таймаут — не ожидание медленного ответа, а признак, что ответа не будет:
 * отказ валидации DTO или сервер до AD-518.
 */
export const CHAT_ACK_TIMEOUT_MS = 10_000;

/** Комнаты события на сокете live-event (`<room>:subscribe`). */
type LiveEventRoom = "metrics" | "chat" | "polls" | "participants";

/**
 * Ack приходит по сети — форма проверяется, а не приписывается. Неузнанная
 * форма — не «ok» и не «отказ»: исход неизвестен, как при таймауте, и
 * вызывающий решает его тем же путём (было ли эхо).
 */
const toChatMessageAck = (ack: unknown): ChatMessageAck => {
  if (typeof ack === "object" && ack !== null) {
    const { status, id, timestamp, error, code, retryAfterMs } = ack as Record<
      string,
      unknown
    >;
    if (
      status === "ok" &&
      typeof id === "string" &&
      typeof timestamp === "string"
    ) {
      return { status: "ok", id, timestamp };
    }
    if (status === "error" && typeof error === "string") {
      return {
        status: "error",
        error,
        ...(typeof code === "string" ? { code } : {}),
        ...(typeof retryAfterMs === "number" ? { retryAfterMs } : {}),
      };
    }
  }
  throw new Error("Неузнанная форма ack chat:message");
};

/** Подписка с приоритетом (управление потоками данных — useRealtimeStreams). */
export interface WebSocketSubscription {
  channel: string;
  streamType?: StreamType;
  priority: number; // 0-100
  filters?: Record<string, any>;
  configuration?: Record<string, any>;
  streamId?: string;
}

export interface OrderUpdateMessage {
  orderId: string;
  status: string;
  previousStatus?: string;
  updatedBy: string;
  timestamp: Date;
  details?: {
    message?: string;
    estimatedTime?: string;
    location?: string;
  };
}

/** Уведомление для useRealTimeNotifications (отличается от backend NotificationEvent). */
export interface NotificationMessage {
  id: string;
  type: "info" | "success" | "warning" | "error";
  title: string;
  message: string;
  userId?: string;
  orderId?: string;
  timestamp: Date;
  read: boolean;
}

export interface AnomalyAlertMessage {
  alert: {
    id: string;
    anomalyId: string;
    severity: "low" | "medium" | "high" | "critical";
    message: string;
    timestamp: string;
    widgetId?: string;
  };
  timestamp: string;
}

export interface AnomalyAlertDismissedMessage {
  alertId: string;
  dismissedAt: string;
  reason?: string;
  widgetId?: string;
  timestamp: string;
}

/** Имена WS-событий, на которые подписываются потребители через on()/off(). */
export const WS_EVENTS = {
  // Connection events
  CONNECTED: "connected",
  DISCONNECTED: "disconnected",
  RECONNECTING: "reconnecting",
  ERROR: "error",

  // Order events (нет backend-gateway — событие никогда не приходит, см. ADR-004)
  ORDER_CREATED: "order:created",
  ORDER_UPDATED: "order:updated",
  ORDER_STATUS_CHANGED: "order:status_changed",
  ORDER_ASSIGNED: "order:assigned",
  ORDER_COMPLETED: "order:completed",
  ORDER_CANCELLED: "order:cancelled",

  // Notification events
  NOTIFICATION_RECEIVED: "notification:received",
  NOTIFICATION_READ: "notification:read",

  // Analytics anomaly events
  ANALYTICS_ANOMALY_ALERT_CREATED: "analytics:anomaly:alert:created",
  ANALYTICS_ANOMALY_ALERT_ACKNOWLEDGED: "analytics:anomaly:alert:acknowledged",
  ANALYTICS_ANOMALY_ALERT_RESOLVED: "analytics:anomaly:alert:resolved",
  ANALYTICS_ANOMALY_ALERT_DISMISSED: "analytics:anomaly:alert:dismissed",
  ANALYTICS_METRIC_UPDATED: "analytics:metric:updated",

  // Live-event namespace events (/api/websocket/events)
  EVENT_METRICS_UPDATE: "metrics:update",
  EVENT_CHAT_MESSAGE: "chat:message",
  EVENT_POLL_UPDATE: "poll:update",
  EVENT_PARTICIPANT_UPDATE: "participant:update",
  EVENT_PARTICIPANTS_LIST: "participants:list",
  EVENT_LIVE_UPDATE: "event:live:update",
} as const;

// Конфигурация WebSocket
interface WebSocketConfig {
  baseUrl: string;
  reconnection: boolean;
  reconnectionAttempts: number;
  reconnectionDelay: number;
  timeout: number;
}

class WebSocketService {
  private notificationSocket: Socket | null = null;
  private analyticsSocket: Socket | null = null;
  private config: WebSocketConfig;
  private connectionStatus: ConnectionStatus = ConnectionStatus.DISCONNECTED;
  private listeners: Map<string, Set<Function>> = new Map();
  private isInitialized = false;
  private isConnecting = false;
  private analyticsQueue: any[] = [];
  private analyticsFlushTimer: any = null;
  private analyticsWindowStart: number = 0;
  private analyticsMessagesInWindow: number = 0;

  // Exponential backoff для переподключения
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 10;
  private reconnectInterval = 1000; // 1 секунда
  private maxReconnectInterval = 30000; // 30 секунд
  private reconnectTimer: NodeJS.Timeout | null = null;

  // ===== Live-event namespace (/api/websocket/events) — ленивое подключение =====
  private eventsSocket: Socket | null = null;
  private eventsHandlersBound = false;
  // AD-516. Во что окно вошло на этом сокете: событие → подписанные комнаты
  // в порядке подписки. Повторяется на каждом переподключении — новое
  // подключение сервер ни в какие комнаты не вводит.
  private liveEventMembership = new Map<string, Set<LiveEventRoom>>();

  // ===== Collaboration namespace ('collaboration') — presence/курсоры/комментарии (W4-S2) =====
  private collaborationSocket: Socket | null = null;
  private collaborationHandlersBound = false;

  // ===== Совместимость: generic priority-подписки (useRealtimeStreams) =====
  // Бэкенд-протокола generic subscribe нет — состояние локальное (REST делает реальную работу).
  private activeSubscriptions: Map<string, WebSocketSubscription> = new Map();
  private pausedSubscriptions: Set<string> = new Set();
  private maxActiveStreams = 10;

  constructor() {
    this.config = {
      baseUrl: process.env["REACT_APP_WS_URL"] || "ws://localhost:3001",
      reconnection: false, // Управляем reconnection вручную для exponential backoff
      reconnectionAttempts: 10, // Увеличено до 10 согласно промту
      reconnectionDelay: 1000, // Начальная задержка 1 секунда
      timeout: 15000,
    };
  }

  /**
   * Инициализация WebSocket соединения
   */
  public async connect(): Promise<void> {
    if (this.isInitialized) {
      logger.info("WebSocket already initialized");
      return;
    }

    if (this.isConnecting) {
      logger.info("WebSocket already connecting");
      return;
    }

    this.isConnecting = true;

    const token = await getAccessToken();

    if (!token) {
      logger.warn("Auth token not found, WS limited mode");
    }

    this.setConnectionStatus(ConnectionStatus.CONNECTING);

    try {
      const wsUrl = this.config.baseUrl;
      logger.info("WS connecting", { wsUrl });

      // Подключение к namespace уведомлений
      // КРИТИЧНО: reconnection: false - управляем вручную для exponential backoff
      this.notificationSocket = io(`${wsUrl}/api/websocket/notifications`, {
        auth: { token: token || "anonymous" },
        reconnection: false, // Управляем reconnection вручную
        timeout: this.config.timeout,
        transports: ["websocket", "polling"],
        forceNew: true,
        autoConnect: false,
      });

      // Подключение к namespace аналитики
      this.analyticsSocket = io(`${wsUrl}/api/websocket/analytics`, {
        auth: { token: token || "anonymous" },
        reconnection: false, // Управляем reconnection вручную
        timeout: this.config.timeout,
        transports: ["websocket", "polling"],
        forceNew: true,
        autoConnect: false,
      });

      this.setupEventHandlers();

      // Пытаемся подключиться с таймаутом
      const connectPromise = new Promise<void>((resolve, reject) => {
        const timeout = setTimeout(() => {
          reject(new Error("WebSocket connection timeout"));
        }, this.config.timeout);

        if (this.notificationSocket) {
          this.notificationSocket.once("connect", () => {
            clearTimeout(timeout);
            resolve();
          });

          this.notificationSocket.once("connect_error", (error) => {
            clearTimeout(timeout);
            captureException(error, { source: "ws", phase: "connect_error" });
            reject(error);
          });
        } else {
          reject(new Error("WebSocket socket не инициализирован"));
        }
      });

      // Начинаем подключение
      this.notificationSocket?.connect();
      this.analyticsSocket?.connect();

      await connectPromise;

      logger.info("WS connected");
      this.isInitialized = true;
      this.isConnecting = false;
    } catch (error) {
      captureException(error, { source: "ws", phase: "connect_catch" });
      logger.warn("WS unavailable, offline mode");
      this.setConnectionStatus(ConnectionStatus.ERROR);
      this.isInitialized = true;
      this.isConnecting = false;
    }
  }

  /**
   * Запланировать переподключение с exponential backoff
   * КРИТИЧНО: Exponential backoff: 1s → 2s → 4s → ... → 30s max
   */
  private scheduleReconnect(): void {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      logger.error("Max reconnect attempts reached");
      this.setConnectionStatus(ConnectionStatus.ERROR);
      return;
    }

    this.clearReconnectTimer();
    this.setConnectionStatus(ConnectionStatus.RECONNECTING);

    // Exponential backoff: 1s, 2s, 4s, 8s, ..., до maxReconnectInterval
    const delay = Math.min(
      this.reconnectInterval * Math.pow(2, this.reconnectAttempts),
      this.maxReconnectInterval,
    );

    logger.info(
      `Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts + 1}/${this.maxReconnectAttempts})`,
    );

    this.reconnectTimer = setTimeout(() => {
      this.reconnectAttempts++;
      this.attemptReconnect();
    }, delay);
  }

  /**
   * Переподключение (внутренний метод для exponential backoff)
   */
  private attemptReconnect(): void {
    if (this.notificationSocket && !this.notificationSocket.connected) {
      this.notificationSocket.connect();
    }
    if (this.analyticsSocket && !this.analyticsSocket.connected) {
      this.analyticsSocket.connect();
    }
  }

  /**
   * Очистить таймер переподключения
   */
  private clearReconnectTimer(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  /**
   * Отключение от WebSocket
   */
  public disconnect(): void {
    this.clearReconnectTimer();
    this.reconnectAttempts = 0;

    if (this.notificationSocket) {
      this.notificationSocket.disconnect();
      this.notificationSocket = null;
    }

    if (this.analyticsSocket) {
      this.analyticsSocket.disconnect();
      this.analyticsSocket = null;
    }

    this.setConnectionStatus(ConnectionStatus.DISCONNECTED);
    logger.info("WS connections closed");
  }

  /**
   * Проверка состояния соединения
   */
  public isConnected(): boolean {
    return (
      this.connectionStatus === ConnectionStatus.CONNECTED &&
      this.notificationSocket?.connected === true
    );
  }

  /**
   * Настройка обработчиков событий
   */
  private setupEventHandlers(): void {
    // Обработчики для уведомлений
    if (this.notificationSocket) {
      this.notificationSocket.on("connect", () => {
        logger.info("WS notifications connected");
        this.reconnectAttempts = 0; // Сброс счетчика при успешном подключении
        this.clearReconnectTimer();
        this.setConnectionStatus(ConnectionStatus.CONNECTED);
        store.dispatch(resetReconnectAttempts());
      });

      this.notificationSocket.on("disconnect", (reason) => {
        logger.warn("WS notifications disconnected", { reason });
        this.setConnectionStatus(ConnectionStatus.DISCONNECTED);

        // Если отключение не по инициативе клиента, пытаемся переподключиться
        if (reason !== "io client disconnect") {
          this.scheduleReconnect();
        }
      });

      this.notificationSocket.on("connect_error", (error) => {
        captureException(error, {
          source: "ws",
          channel: "notifications",
          phase: "connect_error",
        });
        logger.warn("WS notifications connect_error");
        this.setConnectionStatus(ConnectionStatus.ERROR);
        store.dispatch(incrementReconnectAttempts());
        this.scheduleReconnect();
      });

      // События уведомлений с правильными типами
      this.notificationSocket.on(
        "notification:created",
        (data: NotificationEvent) => {
          logger.debug("WS notification:created");
          this.emit("notification:new", data);

          // Проброс в формате NotificationMessage для useRealTimeNotifications (ADR-004)
          const allowed = ["info", "success", "warning", "error"] as const;
          const mappedType = (allowed as readonly string[]).includes(data.type)
            ? (data.type as NotificationMessage["type"])
            : "info";
          const notificationMessage: NotificationMessage = {
            id: data.id,
            type: mappedType,
            title: data.title,
            message: data.message,
            userId: data.userId,
            timestamp: new Date(data.createdAt),
            read: data.read || false,
          };
          this.emit(WS_EVENTS.NOTIFICATION_RECEIVED, notificationMessage);

          // Преобразуем NotificationEvent в INotification
          const notification: INotification = {
            id: data.id,
            title: data.title,
            content: data.message,
            type: "info" as any,
            status: data.read ? "read" : ("unread" as any),
            priority: "medium" as any,
            channels: ["web"] as any,
            userId: data.userId,
            isRead: data.read || false,
            isActive: true,
            createdAt: new Date(data.createdAt),
            updatedAt: new Date(data.createdAt),
          };
          store.dispatch(addNotification(notification));
        },
      );

      this.notificationSocket.on(
        "notification:read",
        (data: { id: string }) => {
          logger.debug("WS notification:read");
          this.emit("notification:read", data);
          // Просто удаляем уведомление из списка непрочитанных
          store.dispatch(removeNotification(data.id));
        },
      );

      this.notificationSocket.on(
        "notification:deleted",
        (data: { id: string }) => {
          logger.debug("WS notification:deleted");
          this.emit("notification:deleted", data);
          store.dispatch(removeNotification(data.id));
        },
      );
    }

    // Обработчики для аналитики
    if (this.analyticsSocket) {
      this.analyticsSocket.on("connect", () => {
        logger.info("WS analytics connected");
      });

      this.analyticsSocket.on("disconnect", (reason) => {
        logger.warn("WS analytics disconnected", { reason });
      });

      this.analyticsSocket.on("connect_error", (error) => {
        captureException(error, {
          source: "ws",
          channel: "analytics",
          phase: "connect_error",
        });
        logger.warn("WS analytics connect_error");
      });

      // События аналитики
      this.analyticsSocket.on(
        "analytics:metric:updated",
        (data: AnalyticsMetricUpdate) => {
          // Rate-limit: не более 50 сообщений за 10 секунд
          const now = Date.now();
          if (now - this.analyticsWindowStart > 10_000) {
            this.analyticsWindowStart = now;
            this.analyticsMessagesInWindow = 0;
          }
          if (this.analyticsMessagesInWindow < 50) {
            this.analyticsMessagesInWindow += 1;
            // Батчим: складируем и отправляем пачкой раз в 500мс
            this.analyticsQueue.push(data);
            this.scheduleAnalyticsFlush();
          } else {
            // Если превысили лимит — откладываем в очередь следующего окна
            this.analyticsQueue.push(data);
            this.scheduleAnalyticsFlush(
              10_000 - (now - this.analyticsWindowStart),
            );
          }
        },
      );

      this.analyticsSocket.on(
        "analytics:metrics:batch",
        (data: AnalyticsBatchUpdate) => {
          // Объединяем с локальной очередью и эмитим единообразно
          const combined: AnalyticsBatchUpdate = {
            batchId: data.batchId,
            timestamp: data.timestamp,
            source: data.source,
            metrics: [...(data.metrics || []), ...this.analyticsQueue],
          };
          this.analyticsQueue = [];
          this.emit("analytics:metrics:batch", combined);
        },
      );

      this.analyticsSocket.on("analytics:dashboard:refresh", (data: any) => {
        logger.debug("WS analytics:dashboard:refresh");
        this.emit("analytics:dashboard:refresh", data);
      });

      this.analyticsSocket.on("analytics:forecast:updated", (data: any) => {
        logger.debug("WS analytics:forecast:updated");
        this.emit("analytics:forecast:updated", data);
      });

      // Anomaly alerts → проброс в listener-map для useRealTimeNotifications (ADR-004)
      this.analyticsSocket.on(
        WS_EVENTS.ANALYTICS_ANOMALY_ALERT_CREATED,
        (data: AnomalyAlertMessage) => {
          this.emit(WS_EVENTS.ANALYTICS_ANOMALY_ALERT_CREATED, data);
        },
      );
      this.analyticsSocket.on(
        WS_EVENTS.ANALYTICS_ANOMALY_ALERT_ACKNOWLEDGED,
        (data: any) => {
          this.emit(WS_EVENTS.ANALYTICS_ANOMALY_ALERT_ACKNOWLEDGED, data);
        },
      );
      this.analyticsSocket.on(
        WS_EVENTS.ANALYTICS_ANOMALY_ALERT_RESOLVED,
        (data: any) => {
          this.emit(WS_EVENTS.ANALYTICS_ANOMALY_ALERT_RESOLVED, data);
        },
      );
      this.analyticsSocket.on(
        WS_EVENTS.ANALYTICS_ANOMALY_ALERT_DISMISSED,
        (data: AnomalyAlertDismissedMessage) => {
          this.emit(WS_EVENTS.ANALYTICS_ANOMALY_ALERT_DISMISSED, data);
        },
      );
    }
  }

  private scheduleAnalyticsFlush(delayMs: number = 500) {
    if (this.analyticsFlushTimer) return;
    this.analyticsFlushTimer = setTimeout(() => {
      try {
        if (this.analyticsQueue.length > 0) {
          const batch: AnalyticsBatchUpdate = {
            batchId: `${Date.now()}`,
            timestamp: new Date().toISOString(),
            source: "realtime",
            metrics: this.analyticsQueue.splice(0),
          };
          this.emit("analytics:metrics:batch", batch);
        }
      } finally {
        this.analyticsFlushTimer = null;
      }
    }, delayMs);
  }

  /**
   * Отправка уведомления
   */
  public emitNotification(event: string, data: any): void {
    if (this.notificationSocket?.connected) {
      this.notificationSocket.emit(event, data);
    } else {
      console.warn("WebSocket не подключен, сохраняем в очередь");
      // Просто логируем, не добавляем в store
    }
  }

  /**
   * Отправка события аналитики
   */
  public emitAnalytics(event: string, data: any): void {
    if (this.analyticsSocket?.connected) {
      this.analyticsSocket.emit(event, data);
    }
  }

  /**
   * Подписка на события
   */
  public on(event: string, callback: Function): void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event)!.add(callback);
  }

  /**
   * Отписка от событий
   */
  public off(event: string, callback: Function): void {
    const eventListeners = this.listeners.get(event);
    if (eventListeners) {
      eventListeners.delete(callback);
      if (eventListeners.size === 0) {
        this.listeners.delete(event);
      }
    }
  }

  /**
   * Внутренний метод для генерации событий
   */
  private emit(event: string, data: any): void {
    const eventListeners = this.listeners.get(event);
    if (eventListeners) {
      eventListeners.forEach((callback) => {
        try {
          callback(data);
        } catch (error) {
          console.error(`Ошибка в обработчике события ${event}:`, error);
        }
      });
    }
  }

  /**
   * Управление статусом соединения
   */
  private setConnectionStatus(status: ConnectionStatus): void {
    const oldStatus = this.connectionStatus;
    this.connectionStatus = status;

    if (oldStatus !== status) {
      this.emit("connection:status", { status, previousStatus: oldStatus });
      // Мост в события нативного клиента (совместимость: useRealTimeOrders/ConnectionStatus)
      if (status === ConnectionStatus.CONNECTED) {
        this.emit(WS_EVENTS.CONNECTED, { timestamp: new Date() });
      } else if (status === ConnectionStatus.DISCONNECTED) {
        this.emit(WS_EVENTS.DISCONNECTED, { timestamp: new Date() });
      } else if (
        status === ConnectionStatus.CONNECTING ||
        status === ConnectionStatus.RECONNECTING
      ) {
        this.emit(WS_EVENTS.RECONNECTING, { attempt: this.reconnectAttempts });
      }
      store.dispatch(setConnectionStatus(status));
    }
  }

  /**
   * Получение текущего статуса
   */
  public getConnectionStatus(): ConnectionStatus {
    return this.connectionStatus;
  }

  /**
   * Повторное подключение
   */
  public async reconnect(): Promise<void> {
    this.disconnect();
    this.isInitialized = false;
    await this.connect();
  }

  /**
   * Подписка на уведомления пользователя
   */
  public subscribeToUserNotifications(userId: string): void {
    this.emitNotification("subscribe:user", { userId });
  }

  /**
   * Отписка от уведомлений пользователя
   */
  public unsubscribeFromUserNotifications(userId: string): void {
    this.emitNotification("unsubscribe:user", { userId });
  }

  /**
   * Подписка на виджет
   */
  public subscribeToWidget(widgetId: string): void {
    this.emitAnalytics("subscribe:widget", { widgetId });
  }

  /**
   * Отписка от виджета
   */
  public unsubscribeFromWidget(widgetId: string): void {
    this.emitAnalytics("unsubscribe:widget", { widgetId });
  }

  /**
   * Подписка на метрики аналитики
   */
  public subscribeToAnalyticsMetrics(
    metrics: string[],
    eventId?: string,
  ): void {
    this.emitAnalytics("subscribe:metrics", { metrics, eventId });
  }

  /**
   * Отписка от метрик аналитики
   */
  public unsubscribeFromAnalyticsMetrics(metrics: string[]): void {
    this.emitAnalytics("unsubscribe:metrics", { metrics });
  }

  /**
   * Подписка на дашборд аналитики
   */
  public subscribeToAnalyticsDashboard(dashboardId: string): void {
    this.emitAnalytics("subscribe:dashboard", { dashboardId });
  }

  /**
   * Отписка от дашборда аналитики
   */
  public unsubscribeFromAnalyticsDashboard(dashboardId: string): void {
    this.emitAnalytics("unsubscribe:dashboard", { dashboardId });
  }

  /**
   * Подписка на прогнозы аналитики
   */
  public subscribeToAnalyticsForecast(eventId?: string): void {
    this.emitAnalytics("subscribe:forecast", { eventId });
  }

  /**
   * Отписка от прогнозов аналитики
   */
  public unsubscribeFromAnalyticsForecast(): void {
    this.emitAnalytics("unsubscribe:forecast", {});
  }

  // ===================================================================
  // Live-event namespace (/api/websocket/events) — ADR-004
  // ===================================================================

  /**
   * Ленивое подключение к namespace live-event. Auth — единый adapter (getAccessToken),
   * токен в handshake.auth.token; userId бэкенд берёт из JWT (XS-008).
   *
   * AD-516. Сокет переподключается сам (встроенный backoff socket.io со
   * случайным разбросом — 10k окон после рестарта сервера не приходят разом),
   * а `auth` — функция: socket.io зовёт её на КАЖДОЙ попытке, и переподключение
   * через час идёт с токеном, который HTTP-слой успел обновить, а не с
   * истёкшим 15-минутным. Сокет создаётся без `await` между проверкой и
   * присваиванием — иначе два вызова подряд создавали два сокета, и события
   * второго, без обработчиков, терялись.
   */
  private async ensureEventsSocket(): Promise<Socket> {
    if (!this.eventsSocket) {
      const wsUrl = this.config.baseUrl;
      this.eventsSocket = io(`${wsUrl}/api/websocket/events`, {
        auth: (cb) => {
          getAccessToken().then(
            (token) => cb({ token: token || "anonymous" }),
            (error: unknown) => {
              // Без токена сервер вход отвергнет; попытка не должна висеть,
              // а сбой хранилища — пропадать молча.
              captureException(error, {
                source: "ws",
                channel: "events",
                phase: "auth_token",
              });
              cb({ token: "anonymous" });
            },
          );
        },
        reconnection: true,
        reconnectionDelay: this.config.reconnectionDelay,
        reconnectionDelayMax: 30_000,
        timeout: this.config.timeout,
        transports: ["websocket", "polling"],
        forceNew: true,
        autoConnect: false,
      });
      this.bindEventsHandlers(this.eventsSocket);
    }
    if (!this.eventsSocket.connected) {
      this.eventsSocket.connect();
    }
    return this.eventsSocket;
  }

  private bindEventsHandlers(socket: Socket): void {
    if (this.eventsHandlersBound) return;
    this.eventsHandlersBound = true;

    // AD-516. Первое подключение уносит то, что экран уже отправил (буфер
    // socket.io); каждое следующее — новое подключение на сервере, без комнат,
    // и вход в событие с подписками повторяется здесь. Без этого окно после
    // обрыва могло писать в чат, но реплик других больше не получало.
    let connectedBefore = false;
    socket.on("connect", () => {
      logger.info("WS events connected");
      if (connectedBefore) {
        this.restoreLiveEventMembership(socket);
      }
      connectedBefore = true;
    });
    socket.on("disconnect", (reason) =>
      logger.warn("WS events disconnected", { reason }),
    );
    socket.on("connect_error", (error) => {
      captureException(error, {
        source: "ws",
        channel: "events",
        phase: "connect_error",
      });
      logger.warn("WS events connect_error");
    });

    // Прозрачный проброс всех серверных событий live-event в listener-map.
    // Дублируем в 'message'-мост ({type,data}) для обобщённых потребителей.
    socket.onAny((event: string, data: any) => {
      this.emit(event, data);
      this.emit("message", { type: event, data });
    });
  }

  /** Повторить вход в события и подписки окна на новом подключении (AD-516). */
  private restoreLiveEventMembership(socket: Socket): void {
    for (const [eventId, rooms] of this.liveEventMembership) {
      socket.emit("event:join", { eventId });
      for (const room of rooms) {
        socket.emit(`${room}:subscribe`, { eventId });
      }
    }
  }

  /** Присоединиться к live-событию (после — подписки на комнаты). */
  public async joinLiveEvent(eventId: string): Promise<void> {
    const socket = await this.ensureEventsSocket();
    if (!this.liveEventMembership.has(eventId)) {
      this.liveEventMembership.set(eventId, new Set());
    }
    socket.emit("event:join", { eventId });
  }

  /** Покинуть live-событие. */
  public leaveLiveEvent(eventId: string): void {
    this.liveEventMembership.delete(eventId);
    this.eventsSocket?.emit("event:leave", { eventId });
  }

  /** Подписаться на комнату события: metrics | chat | polls | participants. */
  public async subscribeLiveEventRoom(
    eventId: string,
    room: LiveEventRoom,
  ): Promise<void> {
    const socket = await this.ensureEventsSocket();
    const rooms = this.liveEventMembership.get(eventId) ?? new Set();
    rooms.add(room);
    this.liveEventMembership.set(eventId, rooms);
    socket.emit(`${room}:subscribe`, { eventId });
  }

  /** Отписаться от комнаты события. */
  public unsubscribeLiveEventRoom(eventId: string, room: LiveEventRoom): void {
    this.liveEventMembership.get(eventId)?.delete(room);
    this.eventsSocket?.emit(`${room}:unsubscribe`, { eventId });
  }

  /**
   * Отправить сообщение в чат события.
   *
   * AD-480: домен `type` сужен до значений колонки `chat_messages.type`.
   * Реплика теперь не ретранслируется, а сохраняется, и WS-DTO бэкенда
   * валидирует `type`; `announcement` в домене колонки отсутствует — такой
   * emit отвергался бы на границе WS.
   *
   * AD-506: из объединения убран `system`. Домен колонки — это то, что вправе
   * хранить база, а не то, что вправе объявить отправитель: реплику с меткой
   * `system` клиент рисует как служебное сообщение платформы, то есть человек
   * выдавал бы своё сообщение за сообщение системы. Бэкенд теперь отвергает её
   * на границе (`@IsIn(CLIENT_SENDABLE_CHAT_MESSAGE_TYPES)`), и оставить
   * значение в сигнатуре здесь значило бы обещать вызов, который приходит
   * обратно ошибкой валидации.
   *
   * AD-518. Возвращает ack сервера: «сохранено» или отказ с причиной. Отказ
   * валидации DTO сервер ack не сопровождает, сервер до AD-518 ack не шлёт
   * вовсе — тогда обещание отклоняется по таймауту `CHAT_ACK_TIMEOUT_MS`, и
   * была ли реплика доставлена (пришло ли её эхо `chat:message`), решает
   * вызывающий.
   */
  public async sendLiveEventChat(
    eventId: string,
    message: string,
    type: "text" | "chat" = "text",
  ): Promise<ChatMessageAck> {
    const socket = await this.ensureEventsSocket();
    const ack: unknown = await socket
      .timeout(CHAT_ACK_TIMEOUT_MS)
      .emitWithAck("chat:message", { eventId, message, type });
    return toChatMessageAck(ack);
  }

  /** Проголосовать в опросе события. */
  public async voteLiveEventPoll(
    eventId: string,
    pollId: string,
    optionId: string,
  ): Promise<void> {
    const socket = await this.ensureEventsSocket();
    socket.emit("poll:vote", { eventId, pollId, optionId });
  }

  /** Отключить namespace live-event (cleanup при размонтировании страницы). */
  public disconnectLiveEvents(): void {
    this.liveEventMembership.clear();
    if (this.eventsSocket) {
      this.eventsSocket.disconnect();
      this.eventsSocket = null;
      this.eventsHandlersBound = false;
    }
  }

  // ===================================================================
  // Collaboration namespace ('collaboration') — W4-S2 (ADR-004, XS-008)
  // ===================================================================

  /**
   * Ленивое подключение к namespace collaboration. Auth — единый adapter (getAccessToken);
   * userId сервер берёт из JWT (XS-008), клиент его не передаёт.
   */
  private async ensureCollaborationSocket(): Promise<Socket> {
    if (!this.collaborationSocket) {
      const token = await getAccessToken();
      const wsUrl = this.config.baseUrl;
      this.collaborationSocket = io(`${wsUrl}/collaboration`, {
        auth: { token: token || "anonymous" },
        reconnection: false,
        timeout: this.config.timeout,
        transports: ["websocket", "polling"],
        forceNew: true,
        autoConnect: false,
      });
      this.bindCollaborationHandlers(this.collaborationSocket);
    }
    if (!this.collaborationSocket.connected) {
      this.collaborationSocket.connect();
    }
    return this.collaborationSocket;
  }

  private bindCollaborationHandlers(socket: Socket): void {
    if (this.collaborationHandlersBound) return;
    this.collaborationHandlersBound = true;

    socket.on("connect", () => logger.info("WS collaboration connected"));
    socket.on("disconnect", (reason) =>
      logger.warn("WS collaboration disconnected", { reason }),
    );
    socket.on("connect_error", (error) => {
      captureException(error, {
        source: "ws",
        channel: "collaboration",
        phase: "connect_error",
      });
      logger.warn("WS collaboration connect_error");
    });

    // Прозрачный проброс всех серверных событий (session:joined, user:joined/left,
    // cursor:update, selection:update, comment:created, reaction:updated, edit:*) в listener-map.
    socket.onAny((event: string, data: any) => {
      this.emit(`collaboration:${event}`, data);
    });
  }

  /** Подписка на событие collaboration (например 'user:joined', 'cursor:update'). */
  public onCollaboration(event: string, callback: Function): void {
    this.on(`collaboration:${event}`, callback);
  }

  public offCollaboration(event: string, callback: Function): void {
    this.off(`collaboration:${event}`, callback);
  }

  /** Присоединиться к сессии совместной работы над ресурсом. */
  public async joinCollaborationSession(
    resourceId: string,
    resourceType: string,
  ): Promise<void> {
    const socket = await this.ensureCollaborationSocket();
    socket.emit("session:join", { resourceId, resourceType });
  }

  /** Покинуть сессию совместной работы. */
  public leaveCollaborationSession(
    resourceId: string,
    resourceType: string,
  ): void {
    this.collaborationSocket?.emit("session:leave", {
      resourceId,
      resourceType,
    });
  }

  /** Трансляция позиции курсора другим участникам. */
  public async sendCollaborationCursor(
    resourceId: string,
    resourceType: string,
    x: number,
    y: number,
    element?: string,
  ): Promise<void> {
    const socket = await this.ensureCollaborationSocket();
    socket.emit("cursor:move", { resourceId, resourceType, x, y, element });
  }

  /** Трансляция выделения элемента. */
  public async sendCollaborationSelection(
    resourceId: string,
    resourceType: string,
    elementId: string,
    startOffset?: number,
    endOffset?: number,
  ): Promise<void> {
    const socket = await this.ensureCollaborationSocket();
    socket.emit("selection:change", {
      resourceId,
      resourceType,
      elementId,
      startOffset,
      endOffset,
    });
  }

  /**
   * Создать live-комментарий через WS (сервер транслирует comment:created в комнату).
   * data: { type, targetId, content, mentions?, position?, attachments? } — authorId с сервера (XS-008).
   */
  public async createCollaborationComment(data: {
    type: string;
    targetId: string;
    content: string;
    mentions?: string[];
    parentId?: string;
    position?: { x: number; y: number; element?: string };
  }): Promise<void> {
    const socket = await this.ensureCollaborationSocket();
    socket.emit("comment:create", data);
  }

  /** Переключить реакцию на комментарий (сервер транслирует reaction:updated). */
  public async toggleCollaborationReaction(
    commentId: string,
    type: string,
  ): Promise<void> {
    const socket = await this.ensureCollaborationSocket();
    socket.emit("reaction:toggle", { commentId, type });
  }

  /** Сигнал начала редактирования элемента. */
  public async startCollaborationEdit(
    resourceId: string,
    resourceType: string,
    elementId: string,
    tool?: string,
  ): Promise<void> {
    const socket = await this.ensureCollaborationSocket();
    socket.emit("edit:start", { resourceId, resourceType, elementId, tool });
  }

  /** Сигнал завершения редактирования элемента (с опциональными изменениями). */
  public async stopCollaborationEdit(
    resourceId: string,
    resourceType: string,
    elementId: string,
    changes?: any,
  ): Promise<void> {
    const socket = await this.ensureCollaborationSocket();
    socket.emit("edit:stop", { resourceId, resourceType, elementId, changes });
  }

  /** Отключить namespace collaboration (cleanup при выходе из сессии). */
  public disconnectCollaboration(): void {
    if (this.collaborationSocket) {
      this.collaborationSocket.disconnect();
      this.collaborationSocket = null;
      this.collaborationHandlersBound = false;
    }
  }

  // ===================================================================
  // Совместимость с удалённым нативным websocket.ts (ADR-004 / XS-011)
  // ===================================================================

  /**
   * Совместимость: единый клиент сам берёт auth (getAccessToken) и сам определяет userId
   * на сервере из JWT (XS-008), поэтому аргументы игнорируются. Просто инициирует connect().
   */
  public initialize(_userId?: string, _accessToken?: string): void {
    void this.connect();
  }

  /**
   * Generic-отправка. Маршрутизируется на notifications socket (mark_read и т.п.).
   * Best-effort: если сокет offline — тихо пропускаем (как и прежнее фактическое поведение).
   */
  public send(message: WebSocketMessage): void {
    if (this.notificationSocket?.connected) {
      this.notificationSocket.emit(message.type, message.data);
    } else {
      logger.debug("WS send skipped (notifications offline)", {
        type: message.type,
      });
    }
  }

  /**
   * Priority-подписка (useRealtimeStreams). Бэкенд-протокола generic subscribe нет —
   * состояние локальное; реальную работу с потоками делает REST (websocket.api.ts).
   */
  public subscribe(subscription: WebSocketSubscription): void {
    const { channel } = subscription;
    if (
      this.activeSubscriptions.size >= this.maxActiveStreams &&
      !this.activeSubscriptions.has(channel)
    ) {
      this.balanceSubscriptions();
    }
    this.activeSubscriptions.set(channel, subscription);
    this.pausedSubscriptions.delete(channel);
    this.emit("activity_logged", { type: "subscription", channel });
  }

  public unsubscribe(channel: string): void {
    this.activeSubscriptions.delete(channel);
    this.pausedSubscriptions.delete(channel);
    this.emit("activity_logged", { type: "unsubscription", channel });
  }

  public updateSubscriptionPriority(channel: string, priority: number): void {
    const sub = this.activeSubscriptions.get(channel);
    if (sub) {
      sub.priority = priority;
      this.activeSubscriptions.set(channel, sub);
      this.emit("activity_logged", { type: "subscription_updated", channel });
    }
  }

  public pauseSubscription(channel: string): void {
    if (this.activeSubscriptions.has(channel)) {
      this.pausedSubscriptions.add(channel);
      this.emit("activity_logged", { type: "subscription_paused", channel });
    }
  }

  public resumeSubscription(channel: string): void {
    if (this.pausedSubscriptions.has(channel)) {
      this.pausedSubscriptions.delete(channel);
      this.emit("activity_logged", { type: "subscription_resumed", channel });
    }
  }

  private balanceSubscriptions(): void {
    const active = Array.from(this.activeSubscriptions.entries())
      .filter(([channel]) => !this.pausedSubscriptions.has(channel))
      .sort(([, a], [, b]) => a.priority - b.priority);
    const overflow = active.length - this.maxActiveStreams;
    if (overflow > 0) {
      active
        .slice(0, overflow)
        .forEach(([channel]) => this.pauseSubscription(channel));
    }
  }

  public setMaxActiveStreams(max: number): void {
    this.maxActiveStreams = max;
    if (this.activeSubscriptions.size > max) {
      this.balanceSubscriptions();
    }
  }

  public getActiveSubscriptions(): WebSocketSubscription[] {
    return Array.from(this.activeSubscriptions.values()).filter(
      (sub) => !this.pausedSubscriptions.has(sub.channel),
    );
  }

  public getPausedSubscriptions(): WebSocketSubscription[] {
    return Array.from(this.activeSubscriptions.values()).filter((sub) =>
      this.pausedSubscriptions.has(sub.channel),
    );
  }

  /** Orders real-time не имеет backend-gateway (ADR-004) — no-op совместимость. */
  public subscribeToOrder(_orderId: string, _priority?: number): void {
    /* no backend orders gateway */
  }

  public unsubscribeFromOrder(_orderId: string): void {
    /* no backend orders gateway */
  }

  /** Совместимость с нативным getConnectionInfo() (ConnectionStatus, useRealTimeOrders). */
  public getConnectionInfo(): {
    status: string;
    reconnectAttempts: number;
    queueSize: number;
    userId: string | null;
    activeSubscriptions: number;
    pausedSubscriptions: number;
    maxActiveStreams: number;
    activityLogSize: number;
  } {
    let userId: string | null = null;
    try {
      userId = (store.getState() as any)?.auth?.user?.id ?? null;
    } catch {
      userId = null;
    }
    return {
      status: this.isConnected() ? "connected" : "disconnected",
      reconnectAttempts: this.reconnectAttempts,
      queueSize: this.analyticsQueue.length,
      userId,
      activeSubscriptions:
        this.activeSubscriptions.size - this.pausedSubscriptions.size,
      pausedSubscriptions: this.pausedSubscriptions.size,
      maxActiveStreams: this.maxActiveStreams,
      activityLogSize: 0,
    };
  }

  /**
   * Получение статистики соединения
   */
  getConnectionStats() {
    return {
      status: this.connectionStatus,
      isConnected: this.isConnected(),
      isInitialized: this.isInitialized,
      notificationSocketConnected: this.notificationSocket?.connected || false,
      analyticsSocketConnected: this.analyticsSocket?.connected || false,
      listenersCount: Array.from(this.listeners.values()).reduce(
        (sum, set) => sum + set.size,
        0,
      ),
    };
  }
}

// Export singleton instance
const webSocketService = new WebSocketService();
export default webSocketService;
